import { useState, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DataGridPro, GridActionsCellItem } from "@mui/x-data-grid-pro";
import Add from "@mui/icons-material/Add";
import Edit from "@mui/icons-material/Edit";
import EventBusy from "@mui/icons-material/EventBusy";
import Delete from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import { standardDateFormat } from "../../actprep-ui/utils/calendar";
import useDisclosure from "../../actprep-ui/hooks/ui/useDisclosure";
import EdgeDrawer from "./EdgeDrawer";
import { useConfirm } from "material-ui-confirm";
import api from "../../actprep-ui/utils/api";
import {
  showSuccessSnackbar,
  showErrorSnackbar,
} from "../../actprep-ui/utils/feedback";

export default function HierarchyTab({ coach, asOfDate, onDataChange }) {
  const confirm = useConfirm();
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [edgeMode, setEdgeMode] = useState(null); // 'supervisor' or 'subordinate'
  const {
    isOpen: drawerOpen,
    onOpen: openDrawer,
    onClose: closeDrawer,
  } = useDisclosure();

  // Fetch active role assignments for this coach
  const { data: roleAssignmentsData } = useGet(
    apiUrls.coaching.roleAssignments(),
    {
      params: {
        search_params: {
          coach_id: coach?.uuid,
          is_active: true,
        },
        keep_related: true,
      },
      enabled: !!coach?.uuid,
    }
  );

  // Fetch reporting edges where this coach's roles are involved
  const { data: edgesData, refetch: refetchEdges } = useGet(
    apiUrls.coaching.reportingEdges(),
    {
      params: {
        keep_related: true,
        search_params: {
          coach_id: coach?.uuid,
        },
      },
      enabled: !!coach?.uuid,
    }
  );

  // Check if a role assignment is active as of the selected date
  const isRoleActiveAsOf = useCallback((role, date) => {
    const startDate = dayjs(role.start_date);
    const endDate = role.end_date ? dayjs(role.end_date) : null;
    return (
      role.is_active &&
      (date.isAfter(startDate, "day") || date.isSame(startDate, "day")) &&
      (!endDate || date.isBefore(endDate, "day") || date.isSame(endDate, "day"))
    );
  }, []);

  // Check if an edge is active as of the selected date
  const isEdgeActiveAsOf = useCallback((edge, date) => {
    const startDate = dayjs(edge.start_date);
    const endDate = edge.end_date ? dayjs(edge.end_date) : null;
    return (
      edge.is_active &&
      (date.isAfter(startDate, "day") || date.isSame(startDate, "day")) &&
      (!endDate || date.isBefore(endDate, "day") || date.isSame(endDate, "day"))
    );
  }, []);

  // Get active role assignments for this coach
  const activeRoleAssignments = useMemo(() => {
    const roleAssignments = roleAssignmentsData?.data || [];
    return roleAssignments.filter((role) => isRoleActiveAsOf(role, asOfDate));
  }, [roleAssignmentsData?.data, asOfDate, isRoleActiveAsOf]);

  // Group edges by role assignments - show ALL active roles, not just those with edges
  const { supervisorRoles, subordinateRoles } = useMemo(() => {
    const allEdges = edgesData?.data || [];
    const supervisorRoles = [];
    const subordinateRoles = [];

    activeRoleAssignments.forEach((role) => {
      // Find edges where this role is a supervisor
      const supervisorEdges = allEdges.filter(
        (edge) =>
          edge.supervisor?.uuid === role.uuid &&
          isEdgeActiveAsOf(edge, asOfDate)
      );

      // Always add supervisor roles (even with no edges) so "Add Subordinate" button shows
      supervisorRoles.push({
        role,
        edges: supervisorEdges,
      });

      // Find edges where this role is a subordinate
      const subordinateEdges = allEdges.filter(
        (edge) =>
          edge.subordinate?.uuid === role.uuid &&
          isEdgeActiveAsOf(edge, asOfDate)
      );

      // Always add subordinate roles (even with no edges) so "Add Supervisor" button shows
      subordinateRoles.push({
        role,
        edges: subordinateEdges,
      });
    });

    return { supervisorRoles, subordinateRoles };
  }, [activeRoleAssignments, edgesData?.data, asOfDate, isEdgeActiveAsOf]);

  const handleAddSubordinate = (supervisorRole) => {
    setEdgeMode("supervisor");
    setSelectedEdge({ supervisor: supervisorRole });
    openDrawer();
  };

  const handleAddSupervisor = (subordinateRole) => {
    setEdgeMode("subordinate");
    setSelectedEdge({ subordinate: subordinateRole });
    openDrawer();
  };

  const handleEditEdge = (edge, edgeMode) => {
    setSelectedEdge(edge);
    setEdgeMode(edgeMode);
    openDrawer();
  };

  const handleEndEdge = async (edge) => {
    try {
      await confirm({
        title: "End Reporting Relationship",
        description:
          "Are you sure you want to end this reporting relationship today?",
        confirmationText: "End Today",
        cancellationText: "Cancel",
      });

      const response = await api.put(
        apiUrls.coaching.reportingEdges(edge.uuid),
        {
          end_date: asOfDate.format("YYYY-MM-DD"),
        }
      );

      // Check the success field in the response
      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason ||
            "Failed to end reporting relationship due to data validation issues"
        );
        return;
      }

      showSuccessSnackbar("Reporting relationship ended successfully");
      refetchEdges();
      onDataChange?.();
    } catch (error) {
      if (error !== undefined) {
        // User didn't cancel
        showErrorSnackbar("Failed to end reporting relationship");
      }
    }
  };

  const handleDrawerSave = () => {
    refetchEdges();
    closeDrawer();
    onDataChange?.();
  };

  const handleDeleteEdge = async (edge) => {
    try {
      await confirm({
        title: "Delete Reporting Relationship",
        description:
          "Are you sure you want to permanently delete this reporting relationship? This action cannot be undone.",
        confirmationText: "Delete",
        cancellationText: "Cancel",
        confirmationButtonProps: {
          color: "error",
          variant: "contained",
        },
      });

      const response = await api.delete(
        apiUrls.coaching.reportingEdges(edge.uuid)
      );

      // Check the success field in the response
      if (response.data.success === false) {
        showErrorSnackbar(
          response.data.reason ||
            "Failed to delete reporting relationship due to data validation issues"
        );
        return;
      }

      showSuccessSnackbar("Reporting relationship deleted successfully");
      refetchEdges();
      onDataChange?.();
    } catch (error) {
      if (error !== undefined) {
        // User didn't cancel
        showErrorSnackbar("Failed to delete reporting relationship");
      }
    }
  };

  const createEdgeColumns = (isSubordinate = false) => [
    {
      field: isSubordinate ? "supervisor" : "subordinate",
      headerName: isSubordinate ? "Manager" : "Employee",
      width: 200,
      valueGetter: (value) => {
        const person = value?.coach;
        const role = value?.role;
        return person
          ? `${person.user?.first_name} ${person.user?.last_name} (${role})`
          : "";
      },
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 120,
      valueGetter: (value) => (value ? dayjs(value) : null),
      valueFormatter: (value) => standardDateFormat(value),
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 120,
      valueGetter: (value) => (value ? dayjs(value) : null),
      renderCell: (params) => (
        <Chip
          label={params.value ? standardDateFormat(params.value) : "Ongoing"}
          color={params.value ? "default" : "success"}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<Edit />}
          label="Edit"
          onClick={() =>
            handleEditEdge(
              params.row,
              isSubordinate ? "subordinate" : "supervisor"
            )
          }
        />,
        <GridActionsCellItem
          key="end"
          icon={<EventBusy />}
          label="End"
          onClick={() => handleEndEdge(params.row)}
          disabled={!params.row.is_active || !!params.row.end_date}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDeleteEdge(params.row)}
        />,
      ],
    },
  ];

  const SupervisorPanel = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        As Manager
      </Typography>

      {supervisorRoles.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              No active manager roles found for the selected date.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {supervisorRoles.map(({ role, edges }) => (
            <Card key={role.uuid}>
              <CardHeader
                title={`${role.role} — ${standardDateFormat(
                  dayjs(role.start_date)
                )} to ${
                  role.end_date
                    ? standardDateFormat(dayjs(role.end_date))
                    : "Ongoing"
                }`}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleAddSubordinate(role)}
                  >
                    Add Employee
                  </Button>
                }
              />
              <CardContent>
                {edges.length > 0 ? (
                  <Box sx={{ height: 300, width: "100%" }}>
                    <DataGridPro
                      rows={edges}
                      columns={createEdgeColumns(false)}
                      getRowId={(row) => row.uuid}
                      hideFooter
                      disableCellFocus
                    />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No employees assigned to this role.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click &quot;Add Employee&quot; to create reporting
                      relationships.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  const SubordinatePanel = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        As Employee
      </Typography>

      {subordinateRoles.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              No active employee roles found for the selected date.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {subordinateRoles.map(({ role, edges }) => (
            <Card key={role.uuid}>
              <CardHeader
                title={`${role.role} — ${standardDateFormat(
                  dayjs(role.start_date)
                )} to ${
                  role.end_date
                    ? standardDateFormat(dayjs(role.end_date))
                    : "Ongoing"
                }`}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={() => handleAddSupervisor(role)}
                  >
                    Add Manager
                  </Button>
                }
              />
              <CardContent>
                {edges.length > 0 ? (
                  <Box sx={{ height: 300, width: "100%" }}>
                    <DataGridPro
                      rows={edges}
                      columns={createEdgeColumns(true)}
                      getRowId={(row) => row.uuid}
                      hideFooter
                      disableCellFocus
                    />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No managers assigned to this role.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click &quot;Add Manager&quot; to create reporting
                      relationships.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SupervisorPanel />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <SubordinatePanel />
        </Grid>
      </Grid>

      <EdgeDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        edge={selectedEdge}
        mode={edgeMode}
        coach={coach}
        asOfDate={asOfDate}
        onSave={handleDrawerSave}
      />
    </Box>
  );
}
